/** Building blocks shared by the Team & roles screens (Figma "Team & roles" frames). */
import { Check, Mail } from "lucide-react";
import type { ReactNode } from "react";

import { Button, cx } from "@/components/ui";
import { initials } from "@/lib/format";

import { roleTone, type RoleTone } from "./roles";

const PILL: Record<RoleTone, string> = {
  owner: "bg-[#fde0dd] text-[#b8291f]",
  admin: "bg-[#dfeafe] text-[#1d4fb8]",
  website: "bg-[#d9f0e6] text-[#13734d]",
  domain: "bg-[#ece4fd] text-[#5b32b4]",
  billing: "bg-[#fce7c6] text-warning-fg",
  analytics: "bg-[#dcf1f7] text-[#0e6a85]",
  neutral: "bg-[#efecea] text-[#5c5452]",
};

const AVATAR: Record<RoleTone, string> = {
  owner: "bg-[#fde4dc] text-[#a8401f]",
  admin: "bg-[#e3edfd] text-[#2c5aa8]",
  website: "bg-[#ddf2e7] text-[#1b7350]",
  domain: "bg-[#ece6fb] text-[#5a3fa6]",
  billing: "bg-[#fbecd2] text-[#8f5a0c]",
  analytics: "bg-[#def1f6] text-[#16677f]",
  neutral: "bg-[#efecea] text-[#5c5452]",
};

export function RolePill({ roleKey, children }: { roleKey: string | undefined; children: ReactNode }) {
  return (
    <span
      className={cx(
        "inline-flex shrink-0 items-center gap-[5px] rounded-full py-[3px] pr-[9px] pl-2 text-[11.5px] font-semibold whitespace-nowrap",
        PILL[roleTone(roleKey)],
      )}
    >
      <span aria-hidden className="size-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

export function Avatar({
  name,
  roleKey,
  size = "md",
}: {
  name: string;
  roleKey?: string;
  size?: "md" | "lg";
}) {
  return (
    <span
      aria-hidden
      className={cx(
        "flex shrink-0 items-center justify-center rounded-full font-semibold",
        size === "lg" ? "size-14 text-base" : "size-9 text-xs",
        AVATAR[roleTone(roleKey)],
      )}
    >
      {initials(name)}
    </span>
  );
}

export function InviteAvatar() {
  return (
    <span
      aria-hidden
      className="flex size-9 shrink-0 items-center justify-center rounded-full border border-dashed border-border text-fg-subtle"
    >
      <Mail className="size-4" />
    </span>
  );
}

/** The bordered checkbox chips of "Other services". */
export function CheckChip({
  checked,
  disabled,
  describedBy,
  onChange,
  children,
}: {
  checked: boolean;
  disabled?: boolean;
  /** Id of the text saying why a chip may be unavailable; linked while it is disabled. */
  describedBy?: string;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label
      className={cx(
        "inline-flex items-center gap-2 rounded-[10px] border border-border px-3 py-2 text-sm font-medium",
        disabled ? "cursor-not-allowed text-fg-subtle opacity-60" : "cursor-pointer text-fg hover:bg-muted",
      )}
    >
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        aria-describedby={disabled ? describedBy : undefined}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden
        className={cx(
          "flex size-[18px] items-center justify-center rounded-[5px] border peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500/40",
          checked ? "border-brand-500 bg-brand-500 text-white" : "border-border bg-surface",
        )}
      >
        {checked && <Check className="size-3" strokeWidth={3} />}
      </span>
      {children}
    </label>
  );
}

/** A tappable row with a title, description and arrow ("Transfer ownership →"). */
export function ChoiceRow({
  title,
  description,
  onClick,
}: {
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <Button
      unstyled
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-border px-4 py-3 text-left hover:bg-muted"
    >
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-fg">{title}</span>
        <span className="mt-0.5 block text-xs text-fg-subtle">{description}</span>
      </span>
      <span aria-hidden className="text-brand-600">
        →
      </span>
    </Button>
  );
}

/** The tinted "What happens" panel of confirmation dialogs. */
export function WhatHappens({ items }: { items: ReactNode[] }) {
  return (
    <div className="rounded-xl border border-border bg-brand-50 px-4 py-3">
      <p className="mb-2 text-xs font-semibold tracking-wide text-fg-subtle uppercase">What happens</p>
      <ul className="list-disc space-y-1.5 pl-4 text-sm text-fg">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}
